import axios, { type AxiosAdapter, type InternalAxiosRequestConfig } from 'axios';
import { deviceLocal } from '@/privacy/deviceStorage';
import {
  TAILNET_PROBE_TIMEOUT_MS,
  createTailnetResolver,
  isTailnetIdentityHost,
  tailnetIdentityOf,
  withTailnetIdentity,
  type ITailnetResolver,
} from '@featit/shared/tailnet-identities';

/**
 * F-0821 (NF-5): this Mac answers on one of two tailnet names, whichever Tailscale login is active.
 * The desktop and any page that names one of them find the one that answers, with no setting.
 *
 * ⛔ Only the **host of one request** moves, and only between the exact names `@featit/shared` lists.
 * `API_BASE_URL`, the stored deployment address and the credential slots keyed by it never change, so a
 * login switch signs nobody out, and a token is never presented to a host outside the list.
 *
 * It sits below the request interceptors, in the adapter, so the sign-in ordering the interceptors keep
 * (I-1380: a request leaves in the call that makes it) holds: with a fresh answer the rewrite is
 * synchronous and the base adapter is called at once. Only the first request of a session, or one after a
 * failure, waits for the probe.
 */
const LAST_GOOD_KEY = 'featit:tailnetLastGood';

function readLastGood(storage: Pick<Storage, 'getItem'> | null): string | null {
  try {
    const host = storage?.getItem(LAST_GOOD_KEY)?.trim().toLowerCase() ?? '';
    return isTailnetIdentityHost(host) ? host : null;
  } catch {
    return null;
  }
}

export interface ITailnetTransportDeps {
  /** The browser's own `fetch`, taken before this module wraps it, so a probe is never rewritten. */
  fetchImpl: typeof fetch;
  storage: Pick<Storage, 'getItem' | 'setItem'> | null;
  /** The host the page was served from: a browser that loaded it can reach it by construction. */
  servedHost: string | null;
}

/** The resolver the transport uses; exported so a test and the sign-in page can read what it settled on. */
export function createTailnetTransportResolver(deps: ITailnetTransportDeps): ITailnetResolver {
  const served = deps.servedHost?.toLowerCase() ?? null;
  return createTailnetResolver({
    probe: async (healthUrl) => {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), TAILNET_PROBE_TIMEOUT_MS);
      try {
        const answer = await deps.fetchImpl(healthUrl, {
          signal: controller.signal,
          credentials: 'omit',
          cache: 'no-store',
        });
        return answer.ok;
      } catch {
        return false;
      } finally {
        clearTimeout(timer);
      }
    },
    initialGoodHost: served && isTailnetIdentityHost(served) ? served : readLastGood(deps.storage),
    initialGoodIsFresh: served !== null && isTailnetIdentityHost(served),
    onGoodHost: (host) => {
      try {
        deps.storage?.setItem(LAST_GOOD_KEY, host);
      } catch {
        // A cache that cannot be written only costs the next start a probe.
      }
    },
  });
}

function requestUrlOf(config: InternalAxiosRequestConfig): string | null {
  const url = config.url ?? '';
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(url)) return url;
  const base = config.baseURL ?? '';
  return base ? `${base.replace(/\/+$/, '')}/${url.replace(/^\/+/, '')}` : null;
}

/** `adapter`, but a request on a listed tailnet name goes to the one that answers. */
export function tailnetAdapter(base: AxiosAdapter, resolver: ITailnetResolver): AxiosAdapter {
  return (config) => {
    const full = requestUrlOf(config);
    if (full === null || tailnetIdentityOf(full) === null) return base(config);
    const sendTo = (target: string) => {
      const host = tailnetIdentityOf(target);
      if (host === null || host === tailnetIdentityOf(full)) return base(config);
      const moved = withTailnetIdentity(full, host);
      return moved === null ? base(config) : base({ ...config, baseURL: undefined, url: moved });
    };
    const attempt = (target: string) =>
      sendTo(target).catch((error: unknown) => {
        if ((error as { response?: unknown } | null)?.response) throw error;
        // Nothing answered on that name: ask again next time, and retry a read once on the other one.
        resolver.forget();
        const method = (config.method ?? 'get').toLowerCase();
        if (method !== 'get' && method !== 'head') throw error;
        return resolver
          .resolve(full)
          .then((next) => (next === target ? Promise.reject(error) : sendTo(next)));
      });
    // A fresh answer leaves in this call; only the first request, or one after a failure, waits.
    const known = resolver.fresh(full);
    return known !== null ? attempt(known) : resolver.resolve(full).then(attempt);
  };
}

/** `fetch`, but a request on a listed tailnet name goes to the one that answers. */
export function tailnetFetch(base: typeof fetch, resolver: ITailnetResolver): typeof fetch {
  return (input, init) => {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : null;
    if (url === null || tailnetIdentityOf(url) === null) return base(input, init);
    return resolver.resolve(url).then((target) => base(target, init));
  };
}

let installed: ITailnetResolver | null = null;

/** Called once, first, by the entry. A no-op on a second call and where there is no window. */
export function installTailnetTransport(): ITailnetResolver | null {
  if (installed || typeof window === 'undefined') return installed;
  const fetchImpl = window.fetch.bind(window);
  const resolver = createTailnetTransportResolver({
    fetchImpl,
    storage: deviceLocal,
    servedHost: window.location.hostname,
  });
  window.fetch = tailnetFetch(fetchImpl, resolver);
  axios.defaults.adapter = tailnetAdapter(axios.getAdapter(axios.defaults.adapter), resolver);
  installed = resolver;
  return resolver;
}
